import { useState } from "react";
import { toast } from "sonner";
import { Pencil, Trash2, Plus, Check, X } from "lucide-react";
import { field, darkBtn } from "@/lib/adminApi";

export default function ThemeManager({ api, themes, items, onChange }) {
  const [name, setName] = useState("");
  const [editing, setEditing] = useState(null);
  const [value, setValue] = useState("");

  const count = (t) => items.filter((m) => m.theme === t).length;
  const run = async (fn, msg) => {
    try {
      await fn();
      toast.success(msg);
      onChange();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Action impossible.");
    }
  };

  const create = () =>
    name.trim() && run(() => api.post("/admin/themes", { name: name.trim() }), "Thématique créée.").then(() => setName(""));
  const rename = (old) =>
    value.trim() && value.trim() !== old
      ? run(() => api.put("/admin/themes", { old, new: value.trim() }), "Thématique renommée.").then(() => setEditing(null))
      : setEditing(null);
  const remove = (t) => run(() => api.delete(`/admin/themes/${encodeURIComponent(t)}`), "Thématique supprimée.");

  return (
    <section data-testid="theme-manager" className="rounded-2xl border border-stone-200/80 bg-white p-5 shadow-sm">
      <h2 className="font-display text-lg font-semibold text-stone-900">Thématiques</h2>
      <div className="mt-4 flex gap-2">
        <input data-testid="theme-new-input" value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && create()} placeholder="Nouvelle thématique" maxLength={60} className={field} />
        <button data-testid="theme-create-button" onClick={create} className={darkBtn} aria-label="Créer">
          <Plus className="h-4 w-4" />
        </button>
      </div>
      <ul className="mt-3 max-h-64 space-y-1.5 overflow-y-auto">
        {themes.map((t) => (
          <li key={t} data-testid={`theme-row-${t}`} className="flex items-center justify-between gap-2 rounded-lg bg-[#FAF8F5] px-3 py-2 text-sm">
            {editing === t ? (
              <>
                <input data-testid={`theme-rename-input-${t}`} autoFocus value={value} onChange={(e) => setValue(e.target.value)} onKeyDown={(e) => e.key === "Enter" && rename(t)} maxLength={60} className="w-full rounded-md border border-stone-200 px-2 py-1 text-sm focus:border-[#C5A059] focus:outline-none" />
                <button data-testid={`theme-rename-save-${t}`} onClick={() => rename(t)} aria-label="Valider"><Check className="h-4 w-4 text-emerald-600" /></button>
                <button onClick={() => setEditing(null)} aria-label="Annuler"><X className="h-4 w-4 text-stone-400" /></button>
              </>
            ) : (
              <>
                <span className="truncate text-stone-700">{t} <span className="text-xs text-stone-400">({count(t)})</span></span>
                <span className="flex shrink-0 gap-2">
                  <button data-testid={`theme-rename-${t}`} onClick={() => { setEditing(t); setValue(t); }} aria-label="Renommer"><Pencil className="h-3.5 w-3.5 text-stone-400 hover:text-[#8a6b2e]" /></button>
                  <button data-testid={`theme-delete-${t}`} onClick={() => remove(t)} disabled={count(t) > 0} title={count(t) ? "Déplacez d'abord ses médias" : "Supprimer"} className="disabled:opacity-30" aria-label="Supprimer"><Trash2 className="h-3.5 w-3.5 text-stone-400 hover:text-[#C85A32]" /></button>
                </span>
              </>
            )}
          </li>
        ))}
        {!themes.length && <li className="text-xs text-stone-400">Aucune thématique.</li>}
      </ul>
    </section>
  );
}
